/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMembers, deleteMembers } from '../../apiServices/workspaceConfig/workspaceConfig';

function OverQuotaModal({
  workspaceDomain, currentCount, maxAllowed, isOwner, onDone,
}) {
  const { t } = useTranslation();
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [kept, setKept] = useState(new Set());

  useEffect(() => {
    if (!workspaceDomain || !isOwner) { setLoadingMembers(false); return; }
    getMembers({
      workspaceName: workspaceDomain, page: 1, pageSize: 200, search: '',
    })
      .then((res) => {
        const list = res.data.members || res.data;
        setMembers(list);
        setKept(new Set(list.map((m) => m.id)));
      })
      .catch(() => setError(t('quota.loadMembersError')))
      .finally(() => setLoadingMembers(false));
  }, [workspaceDomain, isOwner]);

  const toggle = (id, role) => {
    if (role === 'owner') return;
    setKept((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    const toRemove = members.filter((m) => !kept.has(m.id)).map((m) => m.id);
    if (toRemove.length === 0) { onDone(); return; }
    setSaving(true);
    setError(null);
    try {
      await deleteMembers({ workspaceName: workspaceDomain, ids: toRemove });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || t('quota.genericError'));
      setSaving(false);
    }
  };

  const overLimit = kept.size > maxAllowed;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}
    >
      <div style={{
        background: 'var(--main1)',
        border: '1px solid var(--main1Border)',
        borderRadius: '12px',
        padding: '28px',
        minWidth: '340px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px',
        }}
        >
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'var(--special-color)',
            flexShrink: 0,
          }}
          />
          <h2 style={{ margin: 0, fontSize: '17px', color: 'var(--very-visible)' }}>
            {t('quota.membersLimitTitle')}
          </h2>
        </div>

        {/* Description */}
        <p style={{
          margin: '0 0 16px', fontSize: '13px', color: 'var(--visible)', lineHeight: 1.6,
        }}
        >
          {t('quota.membersLimitDescription', { workspaceDomain, currentCount, maxAllowed })}
          {' '}
          {isOwner
            ? t('quota.ownerInstruction')
            : t('quota.nonOwnerInstruction')}
        </p>

        {/* Non-owner: info box + dismiss */}
        {!isOwner && (
          <>
            <div style={{
              background: 'var(--main2)',
              border: '1px solid var(--main1Border)',
              borderRadius: '8px',
              padding: '14px 16px',
              fontSize: '13px',
              color: 'var(--visible)',
              lineHeight: 1.6,
              marginBottom: '16px',
            }}
            >
              {t('quota.nonOwnerInfo')}
            </div>
            <button
              type="button"
              onClick={onDone}
              style={{
                width: '100%',
                padding: '10px 0',
                background: 'var(--main2)',
                color: 'var(--visible)',
                border: '1px solid var(--main1Border)',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              {t('quota.understood')}
            </button>
          </>
        )}

        {/* Owner: member list + confirm */}
        {isOwner && (
          <>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              marginBottom: '16px',
            }}
            >
              {loadingMembers && (
                <p style={{ fontSize: '13px', color: 'var(--visible)', opacity: 0.6 }}>
                  {t('quota.loadingMembers')}
                </p>
              )}
              {members.map((m) => {
                const isMemberOwner = m.role === 'owner';
                const isKept = kept.has(m.id);
                return (
                  <div
                    key={m.id}
                    onClick={() => toggle(m.id, m.role)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${isKept ? 'var(--primary)' : 'var(--main1Border)'}`,
                      background: isKept ? 'rgba(106,106,253,0.06)' : 'transparent',
                      cursor: isMemberOwner ? 'default' : 'pointer',
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      flexShrink: 0,
                      border: `2px solid ${isKept ? 'var(--primary)' : 'var(--main1Border)'}`,
                      background: isKept ? 'var(--primary)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    >
                      {isKept && (
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                          <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--very-visible)' }}>
                        {m.pseudo || m.username}
                      </span>
                      {isMemberOwner && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: 'var(--special-color)',
                          background: 'rgba(106,106,253,0.1)',
                          padding: '1px 6px',
                          borderRadius: '4px',
                        }}
                        >
                          {t('quota.owner')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              fontSize: '13px',
              color: overLimit ? 'var(--special-color)' : 'var(--visible)',
            }}
            >
              <span>
                {kept.size}
                {' '}
                /
                {' '}
                {maxAllowed}
                {' '}
                {t('quota.selected', { plural: kept.size > 1 ? 's' : '' })}
              </span>
              {overLimit && (
                <span>
                  {t('quota.removeMore', {
                    count: kept.size - maxAllowed,
                    plural: kept.size - maxAllowed > 1 ? 's' : '',
                  })}
                </span>
              )}
            </div>

            <button
              type="button"
              disabled={saving || overLimit || loadingMembers}
              onClick={handleConfirm}
              style={{
                width: '100%',
                padding: '10px 0',
                background: overLimit ? 'var(--main1Border)' : 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: saving || overLimit || loadingMembers ? 'not-allowed' : 'pointer',
                opacity: saving || loadingMembers ? 0.7 : 1,
              }}
            >
              {saving ? t('quota.saving') : t('quota.confirmAndContinue')}
            </button>

            {error && (
              <p style={{ marginTop: '10px', color: 'var(--special-color)', fontSize: '13px' }}>
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default OverQuotaModal;
